/**
 * HTTP-клиент с автоматическим обновлением сессии.
 *
 * Access-токен держим только в памяти, а не в localStorage: содержимое
 * localStorage доступно любому скрипту на странице, и при XSS токен утекает.
 * Refresh-токен лежит в httpOnly-cookie, до которой JavaScript не дотягивается,
 * поэтому после перезагрузки страницы сессия восстанавливается запросом /auth/refresh.
 */

const API_BASE = '/api';

let accessToken: string | null = null;

/**
 * Обновление токена может потребоваться сразу нескольким запросам.
 * Держим один общий промис: refresh-токены ротируются, и два параллельных
 * обновления привели бы к тому, что второе ушло бы с уже отозванным токеном
 * и разлогинило пользователя на ровном месте.
 */
let refreshPromise: Promise<boolean> | null = null;

/** Вызывается, когда сессия окончательно потеряна — чтобы UI показал экран входа. */
let onSessionExpired: (() => void) | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function setSessionExpiredHandler(handler: (() => void) | null): void {
  onSessionExpired = handler;
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly details?: string[],
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function rawFetch(path: string, options: RequestInit = {}): Promise<Response> {
  const headers = new Headers(options.headers);
  if (!headers.has('Content-Type') && options.body) {
    headers.set('Content-Type', 'application/json');
  }
  if (accessToken) {
    headers.set('Authorization', `Bearer ${accessToken}`);
  }

  return fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
    credentials: 'include', // без этого браузер не отправит cookie с refresh-токеном
  });
}

async function performRefresh(): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    });
    if (!response.ok) {
      accessToken = null;
      return false;
    }
    const data = (await response.json()) as { accessToken: string };
    accessToken = data.accessToken;
    return true;
  } catch {
    accessToken = null;
    return false;
  }
}

/** Обновить сессию, переиспользуя уже идущий запрос, если он есть. */
export function refreshSession(): Promise<boolean> {
  refreshPromise ??= performRefresh().finally(() => {
    refreshPromise = null;
  });
  return refreshPromise;
}

/**
 * Запрос к API. При 401 один раз пробует обновить сессию и повторить.
 */
export async function apiFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  let response = await rawFetch(path, options);

  // На самих маршрутах авторизации повторять бессмысленно: 401 там означает
  // неверный логин или отсутствующую сессию, а не протухший access-токен.
  const isAuthRoute = path.startsWith('/auth/login') || path.startsWith('/auth/refresh');

  if (response.status === 401 && !isAuthRoute) {
    const refreshed = await refreshSession();
    if (refreshed) {
      response = await rawFetch(path, options);
    } else {
      onSessionExpired?.();
      throw new ApiError('Сессия истекла, войдите заново', 401);
    }
  }

  if (response.status === 204) {
    return undefined as T;
  }

  let payload: unknown = null;
  const text = await response.text();
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      // Не-JSON ответ (например, HTML страницы ошибки прокси)
      if (!response.ok) {
        throw new ApiError(`Ошибка сервера (${response.status})`, response.status);
      }
    }
  }

  if (!response.ok) {
    const body = payload as { error?: string; details?: string[] } | null;
    throw new ApiError(
      body?.error ?? `Ошибка запроса (${response.status})`,
      response.status,
      body?.details,
    );
  }

  return payload as T;
}

/**
 * Файл с сервера (картинка, PDF) с тем же обновлением сессии, что у apiFetch.
 * Обычный <img src> не подходит: access-токен живёт в памяти, и браузер не
 * приложит его к запросу картинки сам.
 */
export async function apiBlob(path: string): Promise<Blob> {
  let response = await rawFetch(path);
  if (response.status === 401) {
    if (!(await refreshSession())) {
      onSessionExpired?.();
      throw new ApiError('Сессия истекла, войдите заново', 401);
    }
    response = await rawFetch(path);
  }
  if (!response.ok) throw new ApiError(`Не удалось загрузить файл (${response.status})`, response.status);
  return response.blob();
}

export const api = {
  get: <T>(path: string) => apiFetch<T>(path),
  post: <T>(path: string, body?: unknown) =>
    apiFetch<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined }),
  put: <T>(path: string, body?: unknown) =>
    apiFetch<T>(path, { method: 'PUT', body: body ? JSON.stringify(body) : undefined }),
  patch: <T>(path: string, body?: unknown) =>
    apiFetch<T>(path, { method: 'PATCH', body: body ? JSON.stringify(body) : undefined }),
  delete: <T>(path: string) => apiFetch<T>(path, { method: 'DELETE' }),
};
